/**
 * MeScreen — личный кабинет.
 *
 * Раньше личное было размазано: настройки профиля жили внутри
 * UserProfilePage (2769 строк), безопасность — в саб-экране чужого
 * раздела «Ещё», кошелёк — отдельной страницей, уведомлений не было
 * вовсе. Контекст «кто я» терялся при каждом переходе.
 *
 * Теперь одна страница с вкладками и ПОСТОЯННОЙ шапкой игрока: аватар,
 * ник, балансы видны всегда. Приём из школы, где 10 роутов свернулись
 * в один кабинет.
 *
 * Вкладка адресуется через ?tab= — на неё можно дать ссылку.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useCabinet } from "../../../context/CabinetContext";
import UserAvatar from "../UserAvatar";
import CabIcon from "../CabIcon";
import { Button, Card, Tabs, EmptyState, fmtNum } from "../../ui";
import ProfileScreen from "./ProfileScreen";
import { AccountSub } from "./MoreScreen";
import WalletScreen from "./WalletScreen";
import FriendsScreen from "./FriendsScreen";
import "../../../styles/cabinet-me.css";

/* icon — готовый элемент, а не имя: компонент Tabs из UI-слоя принимает
   любой узел и не должен знать про конкретный набор иконок проекта. */
const TABS = [
  { id: "profile", label: "Профиль", icon: <CabIcon name="user" size={16} /> },
  { id: "friends", label: "Друзья", icon: <CabIcon name="users" size={16} /> },
  { id: "wallet", label: "Кошелёк", icon: <CabIcon name="diamond" size={16} /> },
  { id: "security", label: "Безопасность", icon: <CabIcon name="shield" size={16} /> },
];

export default function MeScreen() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { displayName, photo, diamonds, stars, rating, profile } = useCabinet();

  const urlTab = params.get("tab");
  const [tab, setTab] = useState(
    TABS.some((t) => t.id === urlTab) ? urlTab : "profile"
  );

  /* Параметр из URL применяем один раз и убираем: иначе F5 залипает
     на вкладке, с которой человек уже ушёл. Приём из школы. */
  useEffect(() => {
    if (urlTab && TABS.some((t) => t.id === urlTab)) {
      setTab(urlTab);
      const next = new URLSearchParams(params);
      next.delete("tab");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);

  if (!isAuthenticated) {
    return (
      <div className="ui-page">
        <EmptyState
          icon={<CabIcon name="user" size={40} />}
          title="Твой кабинет"
          desc="Войди, чтобы управлять профилем, друзьями и кошельком."
          action={
            <Button variant="primary" onClick={() => navigate("/login")}>
              Войти
            </Button>
          }
        />
      </div>
    );
  }

  const frame = profile?.active_frame || "none";

  return (
    <div className="ui-page">
      {/* Шапка видна на ЛЮБОЙ вкладке — так контекст «кто я» не
          теряется при переключении. */}
      <Card className="rb-me__head">
        <UserAvatar
          name={displayName}
          photo={photo}
          size={80}
          frame={frame}
          thickness={4}
        />

        <div className="rb-me__info">
          <h1 className="rb-me__name">{displayName || "Игрок"}</h1>
          {/* Балансы подписаны словами. Раньше рядом стояли три голых
              числа с иконками — «12 / 3 / 480» не читается без легенды,
              особенно когда алмазы и звёзды это РАЗНЫЕ валюты. */}
          <div className="rb-me__stats">
            <span className="rb-me__stat">
              <CabIcon name="diamond" size={16} />
              <b>{fmtNum(diamonds)}</b> алмазов
            </span>
            <span className="rb-me__stat">
              <CabIcon name="star" size={16} />
              <b>{fmtNum(stars)}</b> звёзд
            </span>
            <span className="rb-me__stat">
              <CabIcon name="trophy" size={16} />
              <b>{fmtNum(rating)}</b> рейтинг
            </span>
          </div>
        </div>

        {/* Публичный профиль — отдельная страница: кабинет для
            редактирования, /app/profile/:id для показа другим. */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/app/profile/${user?.userId}`)}
          icon={<CabIcon name="eye" size={15} />}
        >
          Как видят другие
        </Button>
      </Card>

      <Tabs items={TABS} value={tab} onChange={setTab} className="rb-me__tabs" />

      <div className="rb-me__body">
        {tab === "profile" && <ProfileScreen />}
        {tab === "friends" && <FriendsScreen />}
        {tab === "wallet" && <WalletScreen />}
        {tab === "security" && <AccountSub embedded />}
      </div>
    </div>
  );
}
